/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/sheets/QaPhoneMoreSheet.tsx
 * FILE_ID: JOBHUB-QA-PHONE-MORE-001
 * PURPOSE: Relocate every toolbar control the phone header cannot hold.
 */
import { MobileSheet } from '@quotech/ui'
import { QA_PHONE_CONTROLS } from '../qaPhoneControlInventory'
import { requestQaPhonePage } from '../qaPhonePage'
import type { QaBuildView, QaPhoneSheetId } from '../useQaPhoneUi'

export function Row({
  id,
  label,
  onClick,
  disabled,
  testId,
}: {
  id: string
  label: string
  onClick: () => void
  disabled?: boolean
  testId?: string
}) {
  return (
    <button
      type="button"
      data-qa-phone-control={id}
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className="flex min-h-[44px] w-full items-center px-4 text-left text-sm font-medium text-foreground disabled:opacity-40"
    >
      {label}
    </button>
  )
}

export function QaPhoneMoreSheet({
  open,
  onClose,
  onOpen,
  allChecklists,
  onToggleAllChecklists,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  selectPhotos,
  onToggleSelectPhotos,
  onRefresh,
  onSelect,
  addActions,
  onShowTable,
  buildView = 'cards',
  onToggleBuildView,
  jobSections,
  onJobSection,
  onExport,
  onSaveToLibrary,
  canBuild = true,
}: {
  open: boolean
  onClose: () => void
  onOpen: (id: QaPhoneSheetId) => void
  allChecklists: boolean
  onToggleAllChecklists: () => void
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
  selectPhotos: boolean
  onToggleSelectPhotos: () => void
  onRefresh: () => void
  onSelect: () => void
  addActions: {
    addArea: () => void
    addChecklist: () => void
    addProduct: () => void
    standardChecks?: () => void
    addRow: () => void
  }
  onShowTable: () => void
  buildView?: QaBuildView
  /** Absent outside Build mode, where card-vs-table has no meaning. */
  onToggleBuildView?: () => void
  /** Other Job Hub tabs — the phone QA tab hides the strip, so they live here. */
  jobSections?: { key: string; label: string }[]
  onJobSection?: (key: string) => void
  onExport: () => void
  onSaveToLibrary?: () => void
  canBuild?: boolean
}) {
  return (
    <MobileSheet open={open} onClose={onClose} title="More">
      <div className="divide-y divide-border py-1">
        <p className="px-4 pt-3 text-[11px] font-semibold uppercase text-muted-foreground">Scope</p>
        {canBuild ? (
        <Row
          id={QA_PHONE_CONTROLS.prepare}
          label="Start/Add/Edit"
          onClick={() => {
            // Reused from: packages/job-hub/src/jobs/tabs/job-qa/phone/qaPhonePage.ts (requestQaPhonePage)
            requestQaPhonePage('setup')
            onClose()
          }}
        />
        ) : null}
        <Row id={QA_PHONE_CONTROLS.quoteSheet} label="Quote sheet" onClick={() => onOpen('scope-quote')} />
        <Row id={QA_PHONE_CONTROLS.area} label="Area" onClick={() => onOpen('scope-area')} />
        <Row id={QA_PHONE_CONTROLS.checklist} label="Checklist" onClick={() => onOpen('scope-checklist')} />
        <p className="px-4 pt-3 text-[11px] font-semibold uppercase text-muted-foreground">Actions</p>
        <Row
          id={QA_PHONE_CONTROLS.allChecklists}
          label={allChecklists ? 'Showing all checklists' : 'All checklists'}
          onClick={onToggleAllChecklists}
        />
        <Row id={QA_PHONE_CONTROLS.select} label="Select" onClick={onSelect} />
        <Row
          id={QA_PHONE_CONTROLS.selectPhotos}
          label={selectPhotos ? 'Photo select on' : 'Select photos'}
          onClick={onToggleSelectPhotos}
        />
        <Row id={QA_PHONE_CONTROLS.refresh} label="Refresh" onClick={onRefresh} />
        {canBuild ? (
        <>
        <Row id={QA_PHONE_CONTROLS.addArea} label="Add Location" onClick={addActions.addArea} />
        <Row id={QA_PHONE_CONTROLS.addChecklist} label="Add checklist" onClick={addActions.addChecklist} />
        <Row id={QA_PHONE_CONTROLS.templates} label="Templates" onClick={() => onOpen('templates')} />
        <Row id={QA_PHONE_CONTROLS.addProduct} label="Add Product" onClick={addActions.addProduct} />
        {addActions.standardChecks ? (
          <Row id={QA_PHONE_CONTROLS.standardChecks} label="Standard checks" onClick={addActions.standardChecks} />
        ) : null}
        <Row id={QA_PHONE_CONTROLS.rowActions} label="Row actions" onClick={() => onOpen('row-actions')} />
        <Row id={QA_PHONE_CONTROLS.tableColumns} label="Table" onClick={onShowTable} />
        {onToggleBuildView && (
          <Row
            id={QA_PHONE_CONTROLS.buildViewToggle}
            label={buildView === 'cards' ? 'Table view' : 'Card view'}
            onClick={onToggleBuildView}
          />
        )}
        <Row id={QA_PHONE_CONTROLS.newChecklistDraft} label="New checklist draft" onClick={() => onOpen('new-checklist')} />
        </>
        ) : null}
        <p className="px-4 pt-3 text-[11px] font-semibold uppercase text-muted-foreground">Edit</p>
        <Row id={QA_PHONE_CONTROLS.density} label="Density" onClick={() => onOpen('density')} />
        <Row id={QA_PHONE_CONTROLS.undo} label="Undo" disabled={!canUndo} onClick={onUndo} />
        <Row id={QA_PHONE_CONTROLS.redo} label="Redo" disabled={!canRedo} onClick={onRedo} />
        {canBuild ? (
        <>
        <Row id={QA_PHONE_CONTROLS.rename} label="Rename" onClick={() => onOpen('scope-area')} />
        <Row id={QA_PHONE_CONTROLS.move} label="Move" onClick={() => onOpen('move')} />
        <Row id={QA_PHONE_CONTROLS.delete} label="Delete" onClick={() => onOpen('confirm-delete')} />
        <Row id={QA_PHONE_CONTROLS.editChecklist} label="Edit checklist" onClick={() => onOpen('scope-checklist')} />
        <Row id={QA_PHONE_CONTROLS.deleteChecklist} label="Delete checklist" onClick={() => onOpen('confirm-delete')} />
        </>
        ) : null}
        <Row id={QA_PHONE_CONTROLS.assign} label="Assign" onClick={() => onOpen('assign')} />
        <p className="px-4 pt-3 text-[11px] font-semibold uppercase text-muted-foreground">Output</p>
        <Row id={QA_PHONE_CONTROLS.exportItp} label="Export ITP" onClick={onExport} />
        <Row id={QA_PHONE_CONTROLS.saveItpLibrary} label="Save to ITP library" onClick={() => onSaveToLibrary?.()} />
        <Row id={QA_PHONE_CONTROLS.itpLibrary} label="ITP library" onClick={() => onOpen('itp-library')} />
        <Row id={QA_PHONE_CONTROLS.pack} label="Pack" onClick={() => onOpen('pack')} />
        <Row id={QA_PHONE_CONTROLS.notes} label="Notes" onClick={() => onOpen('notes')} />
        <Row id={QA_PHONE_CONTROLS.statusChip} label="Status" onClick={() => onOpen('status')} />
        {jobSections && jobSections.length > 0 && onJobSection ? (
          <>
            <p className="px-4 pt-3 text-[11px] font-semibold uppercase text-muted-foreground">
              Job sections
            </p>
            {jobSections.map((section) => (
              <Row
                key={section.key}
                id={`${QA_PHONE_CONTROLS.jobSection}-${section.key}`}
                label={section.label}
                onClick={() => onJobSection(section.key)}
              />
            ))}
          </>
        ) : null}
      </div>
    </MobileSheet>
  )
}
